import { useEffect, useState } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DEFAULT_COLOUR_RULES } from "./colours";

const MODES = [["qualified", "Qualified view"], ["show_up", "Show-up view"]];
const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const inp = "w-full px-2 py-1 rounded border text-xs bg-transparent";

function RuleEditor({ k, label, r, fields, set }) {
  return (
    <div className="space-y-1.5" data-testid={`colour-rule-${k}`}>
      <div className="text-xs font-medium">{label}</div>
      <label className="block text-[11px] text-neutral-500">Based on field
        <select className={inp} value={r.field} onChange={(e) => set({ ...r, field: e.target.value })} data-testid={`colour-rule-${k}-field`}>
          {!fields.some((f) => f.key === r.field) && <option value={r.field}>{r.field}</option>}
          {fields.map((f) => <option key={f.key} value={f.key} className="bg-black">{f.label || f.key}</option>)}
        </select>
      </label>
      <label className="block text-[11px] text-neutral-500">Values that count as {k === "qualified" ? "Qualified" : "Showed up"} (comma separated)
        <input className={inp} defaultValue={(r.yes || []).join(", ")} onBlur={(e) => set({ ...r, yes: list(e.target.value) })} data-testid={`colour-rule-${k}-yes`} />
      </label>
      <label className="block text-[11px] text-neutral-500">Values that count as {k === "qualified" ? "Not qualified" : "No-show"}
        <input className={inp} defaultValue={(r.no || []).join(", ")} onBlur={(e) => set({ ...r, no: list(e.target.value) })} data-testid={`colour-rule-${k}-no`} />
      </label>
      <div className="text-[10px] text-neutral-600">Anything else shows as {k === "qualified" ? "Not marked yet" : "Show-up not set"}. Matching ignores upper/lower case.</div>
    </div>
  );
}

export default function ColourRules({ rules, onSaved }) {
  const [fields, setFields] = useState([]);
  const [draft, setDraft] = useState(rules);
  useEffect(() => { setDraft(rules); }, [rules]);
  useEffect(() => { api.get("/crm/config").then((r) => setFields((r.data.fields || []).filter((f) => f.key))); }, []);
  const save = async () => {
    const full = Object.fromEntries(MODES.map(([k]) => [k, { ...DEFAULT_COLOUR_RULES[k], ...draft?.[k] }]));
    try { await api.put("/crm/config", { calendar_colours: full }); toast.success("Colour rules saved"); onSaved(full); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="p-1 rounded hover:bg-[var(--panel-2)]" title="Colour rules" data-testid="colour-rules-btn"><Settings2 size={13} /></button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-4 bg-[var(--bg)] border-[var(--border-strong)]" data-testid="colour-rules-panel">
        {MODES.map(([k, l]) => (
          <RuleEditor key={k} k={k} label={l} r={{ ...DEFAULT_COLOUR_RULES[k], ...draft?.[k] }} fields={fields}
            set={(v) => setDraft((d) => ({ ...d, [k]: v }))} />
        ))}
        <button className="btn btn-primary text-xs w-full justify-center" onClick={save} data-testid="colour-rules-save">Save for everyone</button>
      </PopoverContent>
    </Popover>
  );
}
